/**
 * tank-atlas.ts — P01 Texture Atlas helper for tank sprites.
 *
 * The 8 tank sprites (5 player factions + 3 enemy variants) are packed into
 * a single texture atlas (`public/assets/atlas/tanks.png` + `tanks.json`,
 * built by `scripts/build-atlas.cjs`). When the atlas texture is loaded in
 * Phaser, callers should use `add.image(x, y, 'tanks-atlas', frameName)` so
 * the WebGL renderer can batch all tanks into a single draw call.
 *
 * When the atlas is absent (test harness, stripped deployment, or pre-build
 * dev state), we fall back to the per-file textures still preloaded via
 * `load.image(key, url)` — these become `add.image(x, y, key)` calls and
 * each renders as its own draw call (legacy behaviour, no perf regression
 * vs. master).
 *
 * Pure (no Phaser-typed param other than the textures cache) so it's
 * trivially unit-testable.
 */

import { ASSETS } from '../config';

/**
 * Resolve add.image() args for a tank sprite, preferring the atlas when
 * available.
 *
 * @param textures      Phaser textures cache (`scene.textures`) — only
 *                      `.exists()` is called, so a `{exists: (k) => boolean}`
 *                      stub works for tests.
 * @param spriteKey     The legacy per-file texture key (e.g.
 *                      'tank-player-celestial'). The atlas frame name is
 *                      identical to this key by construction (see
 *                      `scripts/build-atlas.cjs`).
 * @returns `{ atlasKey, frame }` when atlas is loaded, or
 *          `{ key }` when only the per-file texture is loaded, or
 *          `null` when neither is available (caller should skip rendering
 *          and keep the Graphics fallback).
 */
export type TankRenderArgs =
  | { mode: 'atlas'; atlasKey: string; frame: string }
  | { mode: 'image'; key: string }
  | null;

export interface TexturesExistsLike {
  exists(key: string): boolean;
}

export function resolveTankRender(
  textures: TexturesExistsLike,
  spriteKey: string,
): TankRenderArgs {
  if (textures.exists(ASSETS.tanksAtlas)) {
    return { mode: 'atlas', atlasKey: ASSETS.tanksAtlas, frame: spriteKey };
  }
  if (textures.exists(spriteKey)) {
    return { mode: 'image', key: spriteKey };
  }
  return null;
}
